import * as Sentry from "@sentry/nextjs"
import { Alert, Spinner } from "flowbite-react"
import { FieldArray, Form, Formik, FormikHelpers, FormikProps } from "formik"
import React, { useMemo, useState } from "react"
import * as Yup from "yup"

import useToast from "@hooks/useToast"
import ParticipantsFields from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipantsFields"
import { checkDisplayRules } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipationDetailsForm"
import { ProductInfoFormAttributes } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ProductInfoForm/ProductInfoForm"
import { PerOrderFieldsFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PerOrderFieldsModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"

export type CustomerDetailsFormAttributes = {
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
}
interface CustomerDetailsFormProps {
  onSuccess?: (arg0: PerOrderFieldsFormAttributes) => void
  selectedCustomerId?: string
  isValid?: boolean
  isIntegratedPage?: boolean
  productFieldsData?: any
  perOrderDetailsRef?: React.RefObject<FormikProps<PerOrderFieldsFormAttributes>>
  productInfo?: ProductInfoFormAttributes
  perOrderInitialValues: PerOrderFieldsFormAttributes
}

const PerOrderDetailsForm = ({
  onSuccess,
  isIntegratedPage = false,
  productFieldsData,
  perOrderDetailsRef,
  productInfo,
  perOrderInitialValues,
}: CustomerDetailsFormProps) => {
  const { showToast } = useToast()
  const [countryCode, setCountryCode] = useState<string>("")
  const [isError, setIsError] = useState("")

  const initialValues = useMemo<PerOrderFieldsFormAttributes | undefined>(() => {
    if (productFieldsData) {
      const participantsFieldsArray: Record<string, boolean | string | string[] | number>[] = []
      const fields: Record<string, boolean | string | string[] | number> = {}
      productFieldsData.forEach((productField) => {
        if (
          productField?.allowed_online_booking &&
          productField?.ask?.includes("ask_per_booking")
        ) {
          fields[productField.field.name] = productField.field.type === "checkbox" ? [] : ""
          fields[`${productField.field.name}_id`] = productField.id ?? ""
        }
      })
      participantsFieldsArray.push(fields)
      return {
        perOrderFieldsDetails:
          perOrderInitialValues?.perOrderFieldsDetails ?? participantsFieldsArray,
      }
    }
  }, [productFieldsData])

  const onSubmit = async (
    values: PerOrderFieldsFormAttributes,
    { setFieldError }: FormikHelpers<PerOrderFieldsFormAttributes>
  ) => {
    try {
      onSuccess?.(values)
      return values
    } catch (e: any) {
      Sentry.captureException(e)
      console.error(e.response.data.message)
      showToast({ heading: e.response.data.message, type: "danger" })
      const errors = e?.response?.data?.errors || []
      for (const key of Object.keys(errors)) {
        setFieldError(key, errors[key][0])
        setIsError(errors[key][0])
      }
    }
  }

  return (
    <>
      {productFieldsData && initialValues ? (
        <>
          {isError && (
            <Alert color={"failure"}>
              <span>{isError}</span>
            </Alert>
          )}
          <Formik
            initialValues={initialValues}
            innerRef={perOrderDetailsRef}
            validationSchema={() =>
              Yup.lazy((formValues: PerOrderFieldsFormAttributes) => {
                if (productFieldsData) {
                  const fields: Record<string, any> = {}
                  productFieldsData
                    .filter(
                      (field) =>
                        checkDisplayRules({
                          field,
                          formValues: formValues?.perOrderFieldsDetails?.[0],
                          ticketInfo: productInfo?.units,
                        }) === "show"
                    )
                    .forEach((productField) => {
                      if (
                        productField.allowed_online_booking &&
                        productField?.ask?.includes("ask_per_booking")
                      ) {
                        if (productField.field.type === "checkbox") {
                          fields[productField.field.name] = productField.required_for_online_booking
                            ? Yup.array()
                                .of(Yup.string())
                                .required(`*${productField.label} Required`)
                                .min(1)
                            : Yup.array().of(Yup.string())
                        } else if (productField.field.type === "inline") {
                          fields[productField.field.name] = Yup.string().optional()
                        } else {
                          fields[productField.field.name] = productField.required_for_online_booking
                            ? Yup.string().required(`*${productField.label} Required`)
                            : Yup.string()
                        }
                      }
                    })
                  return Yup.object().shape({
                    perOrderFieldsDetails: Yup.array()
                      .required()
                      .min(1, "*Atleast one is Required")
                      .of(Yup.object().shape(fields)),
                  })
                }
              })
            }
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({ getFieldProps, values }) => {
              return (
                <Form id={"perOrderFieldsForm"}>
                  <div className={"flex flex-col"}>
                    <div className={"!px-6 !py-4 border rounded-md border-neutral-450"}>
                      <FieldArray {...getFieldProps("perOrderFieldsDetails")}>
                        {() => {
                          if (!values.perOrderFieldsDetails?.length) return
                          return (
                            <>
                              {values.perOrderFieldsDetails.map(
                                (perBookingFieldsDetails, index) => {
                                  return (
                                    <div
                                      className={"md:grid sm:grid-cols-2 sm:gap-4 gap-2"}
                                      key={index}
                                    >
                                      <ParticipantsFields
                                        fieldsData={
                                          productFieldsData.filter(
                                            (field) =>
                                              checkDisplayRules({
                                                field,
                                                formValues: perBookingFieldsDetails,
                                                ticketInfo: productInfo?.units,
                                              }) === "show"
                                          ) ?? []
                                        }
                                        fieldGroupFormName={"perOrderFieldsDetails"}
                                        fieldArrayIndex={0}
                                        type={"ask_per_booking"}
                                        onPhoneInputChange={(value, country) => {
                                          setCountryCode(country.dialCode)
                                        }}
                                        isWidgetPage
                                      />
                                    </div>
                                  )
                                }
                              )}
                            </>
                          )
                        }}
                      </FieldArray>
                    </div>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </>
      ) : null}
    </>
  )
}

export default PerOrderDetailsForm
